import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import type { StripeCardElement } from '@stripe/stripe-js';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { CreditCard, Plus, Trash2, Check } from 'lucide-react';
import { showConfirm } from '../common/AppDialogs';

interface PaymentMethod {
  id: string;
  type: string;
  card: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
  isDefault: boolean;
}

interface PaymentMethodManagerProps {
  organizationId: string;
  isNexusOrg: boolean;
}

export const PaymentMethodManager: React.FC<PaymentMethodManagerProps> = ({
  organizationId,
  isNexusOrg
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load payment methods
  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stripe/payment-methods');
      setPaymentMethods(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to load payment methods:', error);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isNexusOrg) {
      loadPaymentMethods();
    }
  }, [isNexusOrg]);

  // Add new payment method
  const handleAddPaymentMethod = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setAdding(true);
    setError(null);

    try {
      // Create setup intent
      const { data: setupData } = await api.post('/stripe/setup-intent');
      const { clientSecret } = setupData.data;

      const cardElement = elements.getElement(CardElement) as unknown as StripeCardElement | null;
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Confirm setup intent
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        setError(error.message || 'Failed to add payment method');
      } else if (setupIntent?.status === 'succeeded') {
        setShowAddForm(false);
        await loadPaymentMethods();
        // If this is the first payment method, set it as default
        if (paymentMethods.length === 0 && setupIntent.payment_method) {
          await setDefaultPaymentMethod(setupIntent.payment_method as string);
        }
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to add payment method');
    } finally {
      setAdding(false);
    }
  };

  // Set default payment method
  const setDefaultPaymentMethod = async (paymentMethodId: string) => {
    try {
      await api.post(`/stripe/payment-methods/${paymentMethodId}/set-default`);
      await loadPaymentMethods();
    } catch (error: any) {
      setError('Failed to set default payment method');
    }
  };

  // Delete payment method
  const deletePaymentMethod = async (paymentMethodId: string) => {
    if (!(await showConfirm('Are you sure you want to delete this payment method?'))) {
      return;
    }

    try {
      await api.delete(`/stripe/payment-methods/${paymentMethodId}`);
      await loadPaymentMethods();
    } catch (error: any) {
      setError('Failed to delete payment method');
    }
  };

  // Nexus organization message
  if (isNexusOrg) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Method
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            The Nexus organization is not subject to billing and does not require payment methods.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payment Method
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 animate-pulse rounded-lg" />
          </div>
        ) : (
          <>
            {/* Existing Payment Methods */}
            {paymentMethods.length > 0 ? (
              <div className="space-y-3">
                {paymentMethods.map((pm) => (
                  <div
                    key={pm.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">
                          {pm.card?.brand?.toUpperCase()} •••• {pm.card?.last4}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Expires {pm.card?.expMonth}/{pm.card?.expYear}
                          {pm.isDefault && (
                            <span className="ml-2 inline-flex items-center gap-1 text-green-600">
                              <Check className="h-3 w-3" />
                              Default
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!pm.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDefaultPaymentMethod(pm.id)}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deletePaymentMethod(pm.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">No payment methods</p>
                <p className="text-muted-foreground mb-4">
                  Add a payment method to enable billing
                </p>
              </div>
            )}

            {/* Add Payment Method Form */}
            {showAddForm ? (
              <form onSubmit={handleAddPaymentMethod} className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={!stripe || adding}
                    className="flex items-center gap-2"
                  >
                    {adding ? 'Adding...' : 'Add Payment Method'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <Button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Payment Method
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentMethodManager;
